import React, { useState, useEffect, useContext } from "react";
import Table from "rc-table";
import "../../styling/Bookings.css";
import { PiCaretUpDownFill } from "react-icons/pi";
import moment from "moment";
import { EventDataContext } from "../EventDataContext";

const EventsTable = ({ event, onEventClick, setInitialBusinessEventTab }) => {
  // State for tracking current sort column and order
  const [sortOrder, setSortOrder] = useState({});
 
  // State for sorted data (separate from props to avoid direct mutation)
  const [sortedData, setSortedData] = useState(event || []);

  // Context to share event details
  const { EventData, setEventData } = useContext(EventDataContext);

  // --- PAGINATION STATE ---
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10; // Set to 10 entries per page

  // Sync sortedData with incoming props and reset pagination
  useEffect(() => {
    setSortedData(event || []);
    setCurrentPage(1); // Reset to page 1 if the event data changes
  }, [event]);

  // --- PAGINATION MATH ---
  // Calculate total pages based on the current sorted data
  const totalPages = Math.ceil(sortedData.length / pageSize);

  // Slice the data to only show the current page's 10 items
  const paginatedData = sortedData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Sort handler
  const handleSort = (column) => {
    const order = sortOrder[column] === "asc" ? "desc" : "asc";

    setSortOrder({ [column]: order });

    const sorted = [...sortedData].sort((a, b) => {
      let valA = a[column];
      let valB = b[column];

      // Convert strings to lowercase for comparison
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return order === "asc" ? -1 : 1;
      if (valA > valB) return order === "asc" ? 1 : -1;
      return 0;
    });

    setSortedData(sorted); 
  };

  // Columns definition
  const columns = [
    {
      title: (
        <div
          className={`custom-header ${
            sortOrder.column === "eventName" ? "sorted" : ""
          }`}
          onClick={() => handleSort("eventName")}
        >
          <PiCaretUpDownFill className="table-header-icon" />
          Event Name
        </div>
      ),
      dataIndex: "eventName",
      key: "eventName",
      width: 100,
      align: "left",
      render: (text, record) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onEventClick(record._id); 
            setEventData(record);
            setInitialBusinessEventTab(0)
          }}
          style={{ color: "#1C1154", textDecoration: "none" }}
        >
          <h4 style={{ margin: "0px" }}>{text}</h4>
        </a>
      ),
    },
    {
      title: (
        <div
          className={`custom-header ${
            sortOrder.column === "clientName" ? "sorted" : ""
          }`}
          onClick={() => handleSort("clientName")}
        >
          <PiCaretUpDownFill className="table-header-icon" />
          Client Name
        </div>
      ),
      dataIndex: "clientName",
      key: "clientName",
      width: 100,
      align: "left",
    },
    {
      title: (
        <div
          className={`custom-header ${
            sortOrder.column === "status" ? "sorted" : ""
          }`}
          onClick={() => handleSort("status")}
        >
          <PiCaretUpDownFill className="table-header-icon" />
          Event Status
        </div>
      ),
      dataIndex: "status",
      key: "status",
      width: 100,
      align: "left",
      // Added render function to convert status to uppercase
      render: (text) => text ? text.toUpperCase() : "",
    },
    {
      title: (
        <div
          className={`custom-header ${
            sortOrder.column === "expPaymentAmount" ? "sorted" : ""
          }`}
          onClick={() => handleSort("expPaymentAmount")}
        >
          <PiCaretUpDownFill className="table-header-icon" />
          Event Total
        </div>
      ),
      dataIndex: "expPaymentAmount",
      key: "expPaymentAmount",
      width: 100,
      align: "left",
      render: (amount) => `$ ${amount || "0"}`,
    },
    {
      title: (
        <div
          className={`custom-header ${
            sortOrder.column === "eventDate" ? "sorted" : ""
          }`}
          onClick={() => handleSort("eventDate")}
        >
          <PiCaretUpDownFill className="table-header-icon" />
          Event Date
        </div>
      ),
      dataIndex: "eventDate",
      key: "eventDate",
      width: 100,
      align: "left",
      render: (date) => (
        <span>{date ? moment(new Date(date)).format("MMM DD, YYYY") : ""}</span>
      ),
    },
  ];

  const rowClassName = () => "custom-table-row";
  const customRowClassName = () => "table-row-with-border";

  // --- CUSTOM FOOTER FOR PAGINATION ---
  const customFooter = () => (
    <div
      style={{
        paddingBottom: "10px",
        marginLeft: "14px",
        marginRight: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start", // Aligns pagination to the left side
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          style={{
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
            border: "none",
            background: "white",
            padding: "2px 8px",
            color: "#1c1156",
            fontSize: "25px",
            textDecoration: "none",
            transition: "none",
          }}
        >
          {"<"}
        </button>
        <button
          style={{
            background: "#fff",
            color: "#4D4D4D",
            border: "1px solid #D9D9D9",
            fontSize: "20px",
            width: "35px",
            textDecoration: "none",
          }}
        >
          {currentPage}
        </button>
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages || totalPages === 0}
          style={{
            cursor:
              currentPage === totalPages || totalPages === 0
                ? "not-allowed"
                : "pointer",
            border: "none",
            background: "white",
            borderRadius: "0px",
            padding: "2px 8px",
            color: "#1c1156",
            fontSize: "25px",
          }}
        >
          {">"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="events-table">
      <Table
        columns={columns}
        data={paginatedData} // <-- Passed the paginated array instead of the raw sortedData
        scroll={{ x: "100%" }}
        rowClassName={(record, index) =>
          `${rowClassName(record, index)} ${customRowClassName(record, index)}`
        }
        components={{
          header: {
            cell: (props) => (
              <th
                {...props}
                style={{
                  borderBottom: "1px solid #D9D9D9",
                  height: "50px",
                  textAlign: "left",
                }}
              >
                {props.children}
              </th>
            ),
          },
        }}
        footer={customFooter} // <-- Attached the pagination footer
      />
    </div>
  );
};

export default EventsTable;